/**
 * SSR-safe lazy loading utility for React components with **named exports**.
 *
 * This module provides a helper specifically for lazy-loading components exported as
 * named exports (not default exports). It works correctly with @loadable/babel-plugin
 * for server-side rendering by exporting the function as `loadable`, which the babel
 * plugin recognizes through name-based pattern matching.
 *
 * For default exports, use `loadable` from `@loadable/component` directly.
 *
 * @module @shared/core/loadableFromNamedExport
 */

import type { LoadableComponent } from '@loadable/component';
import loadableOriginal from '@loadable/component';
import type { ComponentProps } from 'react';
import type React from 'react';

// Extracts the original props type from React components without the implicit children prop.
// React.FC<Props> automatically adds `children?: ReactNode` to the props type, but we want
// to preserve the exact props interface as defined by the component author.
// This ensures LoadableComponent<Props> matches the original component's props signature.
// - First checks React.FC<P> and extracts P (the original props without children)
// - Then checks React.ComponentType<P> and extracts P (covers other component patterns)
// - Falls back to ComponentProps<T> for intrinsic elements (div, span, etc.)
// - Returns never for non-component types (should not occur in practice)
type ExtractComponentProps<T> =
  T extends React.FC<infer P>
    ? P
    : T extends React.ComponentType<infer P>
      ? P
      : T extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<any> // eslint-disable-line @typescript-eslint/no-explicit-any
        ? ComponentProps<T>
        : never;

/**
 * Create a lazy-loaded React component from a **named export**.
 *
 * This function is SSR-safe because:
 * 1. It's exported with the name `loadable`, which `@loadable/babel-plugin` recognizes
 * 2. The babel plugin performs name-based pattern matching on calls to `loadable()`
 * 3. It uses `resolveComponent` instead of wrapping in `{ default: ... }` to avoid
 *    hoist-non-react-statics race conditions
 *
 * **When to use this module:**
 * - Your component is exported as a **named export** (not default)
 * - Example: `export const MyComponent = () => <div>...</div>`
 *
 * **When NOT to use this module:**
 * - Your component uses a **default export** → Use `loadable` from `@loadable/component` directly
 * - Example: `export default MyComponent` → `import loadable from '@loadable/component'`
 *
 * **Important - Module vs Export Name:**
 * - Module path: `@shared/core/loadableFromNamedExport` (descriptive of purpose)
 * - Export name: `loadable` (required for babel plugin to recognize calls)
 * - Do NOT rename the import: `import { loadable } from '@shared/core/loadableFromNamedExport'`
 *
 * **Using both patterns in the same file:**
 * ```typescript
 * // Rename to avoid name collision
 * import loadableDefault from '@loadable/component';
 * import { loadable } from '@shared/core/loadableFromNamedExport';
 *
 * // For default exports
 * const CompA = loadableDefault(() => import('./CompA'));
 *
 * // For named exports
 * const CompB = loadable(() => import('./CompB'), 'CompB');
 * ```
 *
 * @param importFn - Function that returns a promise resolving to a module
 * @param exportName - Name of the component export in the module
 * @returns A loadable React component with automatically inferred props type
 *
 * @example
 * ```typescript
 * // Component file: MyComponent.tsx
 * export const MyComponent = (props: { title: string }) => <div>{props.title}</div>;
 *
 * // Usage: lazy-load the named export
 * import { loadable } from '@shared/core/loadableFromNamedExport';
 *
 * const MyComponent = loadable(
 *   () => import('./MyComponent'),
 *   'MyComponent'
 * );
 * // TypeScript automatically infers props type from the component
 * ```
 *
 * @see https://loadable-components.com/docs/api-loadable-component/
 * @see https://github.com/gregberge/loadable-components/issues/420
 */
export const loadable = <
  // Modules can export values of any type, so Record<string, any> is necessary here
  TModule extends Record<string, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  TExportName extends keyof TModule & string,
>(
  importFn: () => Promise<TModule>,
  exportName: TExportName,
  options?: Readonly<{
    /**
     * Rendered while the component chunk is being downloaded.
     * Passed directly to @loadable/component's `fallback` option.
     * Use a static (non-animated) skeleton to avoid animation restarts
     * when loadable hands off to the component's own loading state.
     */
    fallback?: JSX.Element;
  }>,
): LoadableComponent<ExtractComponentProps<TModule[TExportName]>> =>
  // Use resolveComponent instead of wrapping in { default: ... } to avoid
  // hoist-non-react-statics race condition with plain object wrappers.
  // See: https://github.com/gregberge/loadable-components/issues/420
  loadableOriginal(importFn, {
    resolveComponent: module => module[exportName],
    ...options,
  });
